import type { Panel } from '../panel.class';
import type { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';
import { getPanelOverlapCfn } from './panel-space.cfn';
import type { PanelLayoutInput } from './plan-group-layout.cfn';

/** A column the distributor positions — vent-only / passthrough-only columns are left alone. */
export const isDistributableGroupCfn = (group: PanelItemGroup): boolean =>
	group.items.children.some((item) => !item.item.category?.isVent && !item.item.category?.isPassthrough);

export interface PanelLayoutInputOptions {
	/**
	 * Treat fine-tuned columns as locked (the distributor's view: a hand-placed
	 * column keeps its spot). Pass false for a preview of a change that will
	 * reset the fine-tunes anyway (a swap, a reorder, an add), so it shows the
	 * even re-spacing that change ends in.
	 */
	honourFineTune?: boolean;
}

/**
 * The planner's view of a panel, built from the live model. The distributor and
 * every "what if" caller (the 3D drag preview) read the panel through this one
 * function so they can never disagree about widths, locks or overlap.
 */
export const getPanelLayoutInputCfn = (panel: Panel, options: PanelLayoutInputOptions = {}): PanelLayoutInput => {
	const { honourFineTune = true } = options;
	const overlap = getPanelOverlapCfn(panel);
	return {
		panelWidth: panel.size.width,
		overlap: { left: overlap.left, right: overlap.right },
		groups: panel.panelItemGroups.children.map((group) => ({
			id: group.id,
			width: group.maxOutsideWidth,
			x: group.center.x,
			locked: group.positionLocked || (honourFineTune && group.fineTuned),
			distributable: isDistributableGroupCfn(group)
		}))
	};
};
